import type { TFunction } from 'i18next'
import {
  birdieGolferCardCopy,
  birdiePastRoundsCopy,
  birdieRoundCardCopy,
  birdieTimeAgo
, feetFromMetres,
  handicapFigure
} from 'src/components/BirdieRoundCard/birdieCopy'
import { getTranslatedMarbleColor } from 'src/services/i18n/utils/gameTranslations'
import type { BirdiePastRound } from './pastRounds'
import { formatMultiplierText, type PuttMark, glyphsForKey } from './tileViewModel'

/**
 * The round card's view model (B19) — one settled Birdie round, as the card
 * draws it, with no React and no `Intl` construction of its own.
 *
 * Owner sign-off 2026-09-08 on `birdie-round-card.tpl.html`: "love it, all
 * good". The card is used three ways — full density inside the fairness modal,
 * compact inside the history sidebar, and (B19 Task 2) as the sidebar's list
 * row — so everything that decides WHAT it says lives here and the component
 * only decides how it looks. That is also what makes the four cases that
 * actually matter testable: two winners, a lone winner, a boosted winner, and a
 * boost that landed on nothing.
 *
 * WHY THE WINNERS ARE THE CARD'S HEADLINE. A Birdie round is repriced every
 * round, so "IN2 paid 5.02x" only means something next to the golfer it was
 * priced against. The card puts the two together: who was putting and how
 * likely they were to hole one, what actually happened, and what the round's own
 * committed prices paid for it.
 */

export type RoundCardDensity = 'full' | 'compact'

/** Which of the two markets a winner tile belongs to. */
// B36: 'round' is the round-type pair. It is a market like the other two — it settles, it pays, and
// a past round has to say which way it went — but it draws a mark instead of putt balls, because it
// describes no putts.
export type RoundCardMarket = 'exact' | 'count' | 'round'

export interface RoundCardWinner {
  market: RoundCardMarket
  /** Set only on a 'round' winner: which side came up, so the tile can draw its mark. */
  roundType?: 'FROST' | 'FIRE'
  /** The balls the tile draws — the pattern, or the count's holed balls. */
  marks: PuttMark[]
  /** The price it paid, formatted (FLOORED to 2 dp, like every board price). */
  price: string
  /** The unboosted price, struck through, when Lightning was on this option. */
  basePrice?: string
  /** "Exact order" / "Any order", or the same with the lightning suffix. */
  tag: string
  lightning: boolean
}

/**
 * The boosted option that did NOT come in — the third, full-width tile.
 *
 * It exists because a Lightning round has to say where its boost went even when
 * the boost went nowhere: a player who backed the boosted option and lost is
 * owed the same account of the round as one who won on it.
 */
export interface RoundCardLightningMiss {
  marks: PuttMark[]
  price: string
  basePrice: string
  tag: string
}

export interface RoundCardView {
  roundId: string
  /** `#1982`, or the first eight of the round's uuid when it has no number. */
  roundLabel: string
  /** "2m ago". Absent on a Lightning round — the gold mark takes its place. */
  timeAgo?: string
  lightning: boolean
  /** The gold eyebrow mark's word, only on a Lightning round. */
  lightningLabel?: string
  golfer: { name: string; initials: string; headshotUrl?: string }
  /** Age / distance / handicap, already formatted, each absent when unknown. */
  figures: { age?: string; distance?: string; handicap?: string }
  /** "70%" — the round's make chance per putt. */
  makeChance: string
  makeChanceCaption: string
  /** The three putts, in putt order. Empty when the pattern was unreadable. */
  marks: PuttMark[]
  /** "Two in" — the count option that came up. */
  outcomeName: string
  /** "Hole · Miss · Hole", or the "no exact pattern" line. */
  patternLine: string
  /** Exact first, then count. One entry on an HHH / MMM round. */
  winners: RoundCardWinner[]
  lightningMiss?: RoundCardLightningMiss
  /** Golfer · outcome, for a control whose visible content is only glyphs. */
  shortLabel: string
}

export interface RoundCardInput {
  round: BirdiePastRound
  t: TFunction
  /** `getTranslatedMarbleColor` — the localised name of a canonical key. */
  labelForKey: (key: string) => string
  /** A fraction (0.7) to "70%", through the caller's ONE `safeLocale` formatter. */
  formatPercent: (fraction: number) => string
  /** An integer, same contract — the age and the putt length. */
  formatInteger: (value: number) => string
  /** The distance line, already built ("3 m") by the golfer-card copy factory. */
  distanceLabel?: string
  /** The handicap chip, likewise ("HCP 12"). */
  handicapLabel?: string
  /** The caption under the make chance. */
  makeChanceCaption: string
  /** `Date.now()` at the host, so every card on screen shares one clock. */
  nowMs: number
}

/**
 * The count option's balls: its HOLED dots only, exactly as the board's count
 * bars draw them (`glyphsForKey`). `IN0` is the one that cannot be drawn as an
 * absence, so it keeps three missed balls.
 */
const marksForKey = (key: string | undefined): PuttMark[] =>
  key === undefined ? [] : (glyphsForKey(key) ?? [])

export function buildRoundCardView(input: RoundCardInput): RoundCardView {
  const { round, t, labelForKey } = input
  const copy = birdieRoundCardCopy(t)
  const pastCopy = birdiePastRoundsCopy(t)

  // Lightning, as the round committed it. `boosts` is empty unless the race row
  // said `is_lightning_round` AND the widget could price the boosted marble, so
  // everything below degrades to "no lightning tile" rather than to a guess.
  const boostByKey = new Map(round.boosts.map((boost) => [boost.key, boost]))
  const exactBoost = round.patternKey === undefined ? undefined : boostByKey.get(round.patternKey)
  const countBoost = round.countKey === undefined ? undefined : boostByKey.get(round.countKey)

  const winners: RoundCardWinner[] = []
  // EXACT first: it is the harder market and the left-hand column. HHH and MMM
  // settle but are never bettable, so `exactPaid` is what decides whether the
  // round has an exact winner at all — not whether the pattern is known.
  if (round.patternKey !== undefined && round.exactPaid !== undefined) {
    const lightning = exactBoost !== undefined
    winners.push({
      market: 'exact',
      marks: round.marks.length > 0 ? round.marks : marksForKey(round.patternKey),
      price: formatMultiplierText(lightning ? exactBoost.paid : round.exactPaid),
      ...(lightning ? { basePrice: formatMultiplierText(exactBoost.base) } : {}),
      tag: lightning ? copy.lightningTag(copy.exactOrder) : copy.exactOrder,
      lightning
    })
  }
  if (round.countKey !== undefined && round.countPaid !== undefined) {
    const lightning = countBoost !== undefined
    winners.push({
      market: 'count',
      marks: marksForKey(round.countKey),
      price: formatMultiplierText(lightning ? countBoost.paid : round.countPaid),
      ...(lightning ? { basePrice: formatMultiplierText(countBoost.base) } : {}),
      tag: lightning ? copy.lightningTag(copy.anyOrder) : copy.anyOrder,
      lightning
    })
  }

  // B36: a frost or fire round names itself among the winners. PLAIN is deliberately NOT shown:
  // most rounds are plain, and a row saying so on every card would be noise rather than a result —
  // the absence is the answer, exactly as it is for a round that priced no boost.
  if (round.roundType === 'FROST' || round.roundType === 'FIRE') {
    const paid = round.roundTypePaid
    winners.push({
      market: 'round',
      marks: [],
      roundType: round.roundType,
      price: paid === undefined ? '' : formatMultiplierText(paid),
      tag: round.roundType === 'FIRE' ? copy.fireRound : copy.frostRound,
      lightning: false
    })
  }

  // The boost that landed on neither winner. The FIRST such option: a round can
  // in principle carry several boosted marbles, and the card has room for one
  // tile — the others are already told by whichever winner carries the seal.
  const missed = round.boosts.find(
    (boost) => boost.key !== round.patternKey && boost.key !== round.countKey
  )

  const hasExactWinner = winners.some((winner) => winner.market === 'exact')
  const patternLabel = round.patternKey === undefined ? undefined : labelForKey(round.patternKey)
  const countLabel = round.countKey === undefined ? undefined : labelForKey(round.countKey)

  const figures: RoundCardView['figures'] = {}
  if (round.age !== undefined) figures.age = input.formatInteger(round.age)
  if (input.distanceLabel !== undefined && round.distanceM !== undefined) {
    figures.distance = input.distanceLabel
  }
  if (input.handicapLabel !== undefined && round.handicap !== undefined) {
    figures.handicap = input.handicapLabel
  }

  const outcomeName = countLabel ?? patternLabel ?? ''

  return {
    roundId: round.roundId,
    // The round number is an IDENTIFIER, not a quantity, so it never goes
    // through the caller's `Intl.NumberFormat`: that formatter groups by
    // default and printed the eyebrow as "#1,982" (de "#1.982", hi
    // "#1,98,234") for the number an operator and the support tool both quote
    // as 1982. The word in front is the spec's and the mockup's, and it is
    // built here rather than in the two components, so the card's eyebrow and
    // the modal's heading cannot phrase the same round differently.
    roundLabel: `${pastCopy.round} ${
      round.roundNumber === undefined ? round.roundId.slice(0, 8) : `#${round.roundNumber}`
    }`,
    // On a Lightning round the gold mark takes the eyebrow's right-hand slot,
    // so the timestamp is not built at all rather than built and hidden.
    ...(round.lightning
      ? { lightning: true, lightningLabel: copy.lightning }
      : { lightning: false, timeAgo: birdieTimeAgo(t, input.nowMs - round.settledAtMs) }),
    golfer: {
      name: round.golfer.name,
      initials: round.golfer.initials,
      ...(round.golfer.headshotUrl === undefined
        ? {}
        : { headshotUrl: round.golfer.headshotUrl })
    },
    figures,
    makeChance: input.formatPercent(round.makeRatePct / 100),
    makeChanceCaption: input.makeChanceCaption,
    marks: round.marks,
    outcomeName,
    // The template's own rule: the pattern line is the exact market's name, and
    // a round with no exact option says so instead of naming a sequence nobody
    // could have backed. The three slots above already show what happened.
    patternLine: hasExactWinner && patternLabel !== undefined ? patternLabel : copy.noExactPattern,
    winners,
    ...(missed === undefined
      ? {}
      : {
          lightningMiss: {
            marks: marksForKey(missed.key),
            price: formatMultiplierText(missed.paid),
            basePrice: formatMultiplierText(missed.base),
            tag: copy.lightningMissed
          }
        }),
    // A dot-separated join, not a sentence: nothing here is grammatical in any
    // locale, so nothing here needs translating (the strip's own precedent).
    shortLabel: [round.golfer.name, outcomeName].filter(Boolean).join(' · ')
  }
}

/**
 * A whole list of rounds, keyed by round id (B19 Task 2).
 *
 * THREE surfaces draw the same card now — the disc column's modal, the history
 * sidebar's compact rows, and the modal those rows open — and each of them needs
 * the round's own golfer-card copy ("3 m", "HCP 12") built per round. Written
 * once here, so a fourth surface cannot phrase a figure line differently from
 * the third, and so the two things a caller genuinely owns — its locale-safe
 * formatters and its clock — stay the only inputs it has to get right.
 *
 * Still pure and still free of `Intl`: `formatPercent` / `formatInteger` arrive
 * from the caller's ONE `safeLocale`-coerced formatter pair (`en-soc` is a real
 * language with an invalid BCP 47 tag and every Intl constructor throws on it).
 */
export function buildRoundCardViews(input: {
  rounds: readonly BirdiePastRound[]
  t: TFunction
  formatPercent: (fraction: number) => string
  formatInteger: (value: number) => string
  /** ONE clock for every card in the list, so no two rows disagree on "now". */
  nowMs: number
}): Map<string, RoundCardView> {
  const { t, formatPercent, formatInteger, nowMs } = input
  // The CARD's caption, not the board tile's: the tile has a pill to caption
  // and can carry "make chance per putt", the card has a fixed box beside a
  // 24-px figure and could not.
  const makeChanceCaption = birdieRoundCardCopy(t).makeChance
  const out = new Map<string, RoundCardView>()
  for (const round of input.rounds) {
    // The two figure strings go through the golfer card's own copy factory, so
    // "3 m" and "HCP 12" are the SAME sentences the capsule renders rather than
    // a second pair of literals for the extractor to carry.
    const cardCopy = birdieGolferCardCopy(t, {
      handicap: handicapFigure(round.handicap),
      hole: '',
      distance: round.distanceM === undefined ? '' : formatInteger(feetFromMetres(round.distanceM))
    })
    out.set(
      round.roundId,
      buildRoundCardView({
        round,
        t,
        labelForKey: (key) => getTranslatedMarbleColor(key, t),
        formatPercent,
        formatInteger,
        distanceLabel: cardCopy.distance,
        handicapLabel: cardCopy.handicap,
        makeChanceCaption,
        nowMs
      })
    )
  }
  return out
}
